import { type FC, useEffect, useRef } from "react";

import { Plus, Trash2, AlertTriangle } from "lucide-react";

import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { IconButton } from "@/shared/components/ui/icon-button";
import { OptionCard } from "@/shared/components/ui/OptionCard";
import { RadioGroup } from "@/shared/components/ui/radio-group";
import { FieldError } from "@/shared/components/ui/FieldError";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import { useJsonListAnswer } from "@/shared/hooks/useJsonListAnswer";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

import { type AdminChildRow, createEmptyChild } from "./adminFamilyTypes";

const PARENTS_KEY = "admin_family_has_living_parents";
const PARENTS_LIST_KEY = "admin_family_parents_list";
const SIBLINGS_KEY = "admin_family_has_siblings";
const SIBLINGS_LIST_KEY = "admin_family_siblings_list";

function createEmptyParent(): AdminChildRow {
  return { ...createEmptyChild(), isMinor: "no" };
}

function seedParentsFromContacts(contacts: { first_name: string | null; last_name: string | null; person_status: string | null; relationship: string | null }[]): AdminChildRow[] {
  return contacts
    .filter((c) => c.relationship?.toLowerCase() === "parent")
    .map((c) => ({
      ...createEmptyParent(),
      firstName: c.first_name ?? "",
      lastName: c.last_name ?? "",
      status: c.person_status === "passed_away" ? "passed_away" : c.person_status === "living" ? "living" : "",
    }));
}

interface ParentCardProps {
  parent: AdminChildRow;
  index: number;
  onChange: (index: number, updated: AdminChildRow) => void;
  errors?: Record<string, string>;
  errPrefix: string;
}

const ParentCard: FC<ParentCardProps> = ({ parent, index, onChange, errors, errPrefix }) => (
  <div className="flex flex-col gap-3 rounded-lg border border-primary p-4">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="flex flex-col gap-1.5">
        <Label>First name</Label>
        <Input
          placeholder="First name"
          value={parent.firstName}
          onChange={(e) => onChange(index, { ...parent, firstName: e.target.value })}
          className={errors?.[`${errPrefix}_${index}_first_name`] ? "border-destructive" : ""}
        />
        <FieldError message={errors?.[`${errPrefix}_${index}_first_name`]} />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label>Last name</Label>
        <Input
          placeholder="Last name"
          value={parent.lastName}
          onChange={(e) => onChange(index, { ...parent, lastName: e.target.value })}
          className={errors?.[`${errPrefix}_${index}_last_name`] ? "border-destructive" : ""}
        />
        <FieldError message={errors?.[`${errPrefix}_${index}_last_name`]} />
      </div>
    </div>
    <div className="flex flex-col gap-1.5">
      <Label>Status</Label>
      <Select value={parent.status} onValueChange={(v) => onChange(index, { ...parent, status: v })}>
        <SelectTrigger className={errors?.[`${errPrefix}_${index}_status`] ? "border-destructive" : ""}>
          <SelectValue placeholder="Select" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="living">Living</SelectItem>
          <SelectItem value="passed_away">Passed away</SelectItem>
        </SelectContent>
      </Select>
      <FieldError message={errors?.[`${errPrefix}_${index}_status`]} />
    </div>
  </div>
);

interface SiblingCardProps {
  sibling: AdminChildRow;
  index: number;
  canRemove: boolean;
  onChange: (index: number, updated: AdminChildRow) => void;
  onRemove: (index: number) => void;
  errors?: Record<string, string>;
  errPrefix: string;
}

const SiblingCard: FC<SiblingCardProps> = ({
  sibling,
  index,
  canRemove,
  onChange,
  onRemove,
  errors,
  errPrefix,
}) => {
  const updateField = (field: keyof AdminChildRow, value: string) => {
    const updated = { ...sibling, [field]: value };
    if (field === "status" && value !== "passed_away") {
      updated.hasChildren = "";
      updated.children = [];
    }
    onChange(index, updated);
  };

  const handleHasChildrenChange = (value: string) => {
    const updated = { ...sibling, hasChildren: value };
    if (value === "yes" && sibling.children.length === 0) {
      updated.children = [createEmptyChild()];
    } else if (value !== "yes") {
      updated.children = [];
    }
    onChange(index, updated);
  };

  return (
    <div className="relative flex flex-col gap-4 rounded-lg border border-primary p-6 bg-white">
      {canRemove && (
        <div className="absolute right-2 top-2">
          <IconButton label="Remove" variant="neutral" onClick={() => onRemove(index)}>
            <Trash2 />
          </IconButton>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label>First name</Label>
          <Input
            placeholder="First name"
            value={sibling.firstName}
            onChange={(e) => updateField("firstName", e.target.value)}
            className={errors?.[`${errPrefix}_${index}_first_name`] ? "border-destructive" : ""}
          />
          <FieldError message={errors?.[`${errPrefix}_${index}_first_name`]} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>Last name</Label>
          <Input
            placeholder="Last name"
            value={sibling.lastName}
            onChange={(e) => updateField("lastName", e.target.value)}
            className={errors?.[`${errPrefix}_${index}_last_name`] ? "border-destructive" : ""}
          />
          <FieldError message={errors?.[`${errPrefix}_${index}_last_name`]} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label>Are they under 18 years old?</Label>
          <Select value={sibling.isMinor} onValueChange={(v) => updateField("isMinor", v)}>
            <SelectTrigger className={errors?.[`${errPrefix}_${index}_is_minor`] ? "border-destructive" : ""}>
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
          <FieldError message={errors?.[`${errPrefix}_${index}_is_minor`]} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>Status</Label>
          <Select value={sibling.status} onValueChange={(v) => updateField("status", v)}>
            <SelectTrigger className={errors?.[`${errPrefix}_${index}_status`] ? "border-destructive" : ""}>
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="living">Living</SelectItem>
              <SelectItem value="passed_away">Passed away</SelectItem>
            </SelectContent>
          </Select>
          <FieldError message={errors?.[`${errPrefix}_${index}_status`]} />
        </div>
      </div>

      {sibling.status === "passed_away" && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label>Did {sibling.firstName || "this sibling"} have children?</Label>
            <Select value={sibling.hasChildren} onValueChange={handleHasChildrenChange}>
              <SelectTrigger className={errors?.[`${errPrefix}_${index}_has_children`] ? "border-destructive" : ""}>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
            <FieldError message={errors?.[`${errPrefix}_${index}_has_children`]} />
            <p className="text-xs text-tertiary">
              If {sibling.firstName || "they"} did, their children take that place on the court's list.
            </p>
          </div>

          {sibling.hasChildren === "yes" && (
            <div className="flex flex-col gap-4">
              <p className="font-sans text-base font-semibold">
                Add {sibling.firstName || "their"}'s children
              </p>

              {sibling.children.map((nephew, ni) => {
                const gcPrefix = `${errPrefix}_${index}_gc`;
                return (
                  <div key={nephew.id} className="flex flex-col gap-3 rounded-lg border border-primary bg-subtle p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="flex flex-col gap-1.5">
                        <Label>First name</Label>
                        <Input
                          placeholder="First name"
                          value={nephew.firstName}
                          onChange={(e) => {
                            const next = [...sibling.children];
                            next[ni] = { ...nephew, firstName: e.target.value };
                            onChange(index, { ...sibling, children: next });
                          }}
                          className={errors?.[`${gcPrefix}_${ni}_first_name`] ? "border-destructive" : ""}
                        />
                        <FieldError message={errors?.[`${gcPrefix}_${ni}_first_name`]} />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <Label>Last name</Label>
                        <Input
                          placeholder="Last name"
                          value={nephew.lastName}
                          onChange={(e) => {
                            const next = [...sibling.children];
                            next[ni] = { ...nephew, lastName: e.target.value };
                            onChange(index, { ...sibling, children: next });
                          }}
                          className={errors?.[`${gcPrefix}_${ni}_last_name`] ? "border-destructive" : ""}
                        />
                        <FieldError message={errors?.[`${gcPrefix}_${ni}_last_name`]} />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="flex flex-col gap-1.5">
                        <Label>Are they under 18 years old?</Label>
                        <Select
                          value={nephew.isMinor}
                          onValueChange={(v) => {
                            const next = [...sibling.children];
                            next[ni] = { ...nephew, isMinor: v };
                            onChange(index, { ...sibling, children: next });
                          }}
                        >
                          <SelectTrigger className={errors?.[`${gcPrefix}_${ni}_is_minor`] ? "border-destructive" : ""}>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="yes">Yes</SelectItem>
                            <SelectItem value="no">No</SelectItem>
                          </SelectContent>
                        </Select>
                        <FieldError message={errors?.[`${gcPrefix}_${ni}_is_minor`]} />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <Label>Status</Label>
                        <Select
                          value={nephew.status}
                          onValueChange={(v) => {
                            const next = [...sibling.children];
                            next[ni] = { ...nephew, status: v };
                            onChange(index, { ...sibling, children: next });
                          }}
                        >
                          <SelectTrigger className={errors?.[`${gcPrefix}_${ni}_status`] ? "border-destructive" : ""}>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="living">Living</SelectItem>
                            <SelectItem value="passed_away">Passed away</SelectItem>
                          </SelectContent>
                        </Select>
                        <FieldError message={errors?.[`${gcPrefix}_${ni}_status`]} />
                      </div>
                    </div>
                  </div>
                );
              })}

              <button
                type="button"
                onClick={() =>
                  onChange(index, { ...sibling, children: [...sibling.children, createEmptyChild()] })
                }
                className="flex items-center gap-1.5 text-sm font-medium text-brand hover:underline"
              >
                <Plus className="size-3.5" />
                Add another child
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const AdminExtendedFamilyScreen: FC<FlowCustomComponentProps> = ({
  answers,
  setAnswer,
  errors,
}) => {
  const { person } = useAppContext();
  const name = person?.first_name ?? "the decedent";

  const married = answers["admin_family_married_at_death"] as string;
  const spouseStatus = answers["admin_family_spouse_status"] as string;
  const hasLivingSpouse = married === "yes" && spouseStatus === "living";
  const isConditionalParentsMode = hasLivingSpouse;

  const hasLivingParents = (answers[PARENTS_KEY] as string) ?? "";
  const hasSiblings = (answers[SIBLINGS_KEY] as string) ?? "";

  const [parentsList, syncParents] = useJsonListAnswer<AdminChildRow>(
    answers, PARENTS_LIST_KEY, setAnswer as (key: string, value: string) => void,
  );
  const [siblingsList, syncSiblings] = useJsonListAnswer<AdminChildRow>(
    answers, SIBLINGS_LIST_KEY, setAnswer as (key: string, value: string) => void,
  );

  const handleParentsSelect = (value: string) => {
    setAnswer(PARENTS_KEY, value);
    if (value === "yes" && parentsList.length === 0) {
      const seeded = seedParentsFromContacts(contacts);
      if (seeded.length > 0) {
        seededParentsRef.current = true;
        syncParents(seeded);
      } else {
        syncParents([createEmptyParent()]);
      }
    } else if (value !== "yes") {
      syncParents([]);
      seededParentsRef.current = false;
    }
  };

  const handleSiblingsSelect = (value: string) => {
    setAnswer(SIBLINGS_KEY, value);
    if (value === "yes" && siblingsList.length === 0) {
      syncSiblings([createEmptyChild()]);
    } else if (value !== "yes") {
      syncSiblings([]);
    }
  };

  const handleParentChange = (index: number, updated: AdminChildRow) => {
    syncParents((prev) => prev.map((p, i) => (i === index ? updated : p)));
  };

  const handleSiblingChange = (index: number, updated: AdminChildRow) => {
    syncSiblings((prev) => prev.map((s, i) => (i === index ? updated : s)));
  };

  const removeSibling = (index: number) => {
    syncSiblings((prev) => prev.filter((_, i) => i !== index));
  };

  const { contacts } = useUserContacts();

  // Auto-seed parent rows from contacts or empty in conditional mode
  const parentsLengthRef = useRef(parentsList.length);
  parentsLengthRef.current = parentsList.length;
  const syncParentsRef = useRef(syncParents);
  syncParentsRef.current = syncParents;
  const seededParentsRef = useRef(false);

  useEffect(() => {
    if (seededParentsRef.current || parentsLengthRef.current > 0) return;
    if (!isConditionalParentsMode && hasLivingParents !== "yes") return;

    const seeded = seedParentsFromContacts(contacts);
    if (seeded.length > 0) {
      seededParentsRef.current = true;
      syncParentsRef.current(seeded);
      if (!isConditionalParentsMode) setAnswer(PARENTS_KEY, "yes");
    } else if (isConditionalParentsMode) {
      seededParentsRef.current = true;
      syncParentsRef.current([createEmptyParent()]);
    }
  }, [isConditionalParentsMode, hasLivingParents, contacts, setAnswer]);

  // Auto-seed sibling rows from contacts
  const seededSiblingsRef = useRef(false);
  useEffect(() => {
    if (seededSiblingsRef.current || siblingsList.length > 0 || hasSiblings) return;
    const siblingContacts = contacts.filter((c) => c.relationship?.toLowerCase() === "sibling");
    if (siblingContacts.length === 0) return;
    seededSiblingsRef.current = true;

    const seeded = siblingContacts.map((c) => ({
      ...createEmptyChild(),
      firstName: c.first_name ?? "",
      lastName: c.last_name ?? "",
      status: c.person_status === "passed_away" ? "passed_away" : c.person_status === "living" ? "living" : "",
      isMinor: c.additional_details?.includes("minor") ? "yes" : "",
    }));
    setAnswer(SIBLINGS_KEY, "yes");
    syncSiblings(seeded);
  }, [contacts, siblingsList.length, hasSiblings, setAnswer, syncSiblings]);

  const noLivingParent =
    hasLivingParents === "no_living_parents" ||
    (hasLivingParents === "yes" && parentsList.length > 0 && parentsList.every((p) => p.status === "passed_away"));
  const showSiblings = !isConditionalParentsMode && noLivingParent;

  if (isConditionalParentsMode) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <p className="font-sans text-xl font-semibold leading-[30px]">
            {name}'s parents
          </p>
          <p className="text-sm text-secondary tracking-[-0.28px]">
            With a spouse and no surviving children, court rules require {name}'s
            parents on the family map. They will receive formal notice by mail,
            but nothing else is needed from them.
          </p>
        </div>

        {parentsList.map((parent, i) => (
          <ParentCard
            key={parent.id}
            parent={parent}
            index={i}
            onChange={handleParentChange}
            errors={errors}
            errPrefix="parent"
          />
        ))}

        <button
          type="button"
          onClick={() => syncParents([...parentsList, createEmptyParent()])}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-primary py-3 text-sm font-medium text-primary hover:bg-subtle"
        >
          <Plus className="size-4" />
          Add another
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-1">
        <p className="font-sans text-xl font-semibold leading-[30px]">
          {name}'s closest family
        </p>
        <p className="text-sm text-secondary tracking-[-0.28px]">
          With no surviving spouse or children, state law looks to {name}'s parents next,
          then brothers and sisters, to determine who can serve as administrator.
        </p>
      </div>

      <div className="flex flex-col gap-4">
        <div className="border-t border-primary" />

        <div className="flex flex-col gap-1">
          <p className="font-sans text-xl font-semibold leading-[30px]">
            Is either of {name}'s parents living?
          </p>
          <p className="text-sm text-secondary tracking-[-0.28px]">
            Include biological and adoptive parents. The court asks for both.
          </p>
        </div>

        <RadioGroup
          name="has_living_parents"
          value={hasLivingParents}
          onValueChange={handleParentsSelect}
          className="flex flex-col gap-3"
        >
          <OptionCard label="Yes" selected={hasLivingParents === "yes"} onClick={() => handleParentsSelect("yes")} variant="radio" value="yes" />
          <OptionCard label="No living parents" selected={hasLivingParents === "no_living_parents"} onClick={() => handleParentsSelect("no_living_parents")} variant="radio" value="no_living_parents" />
        </RadioGroup>
        <FieldError message={errors?.has_living_parents} />

        {hasLivingParents === "yes" && (
          <div className="flex flex-col gap-4">
            <div className="border-t border-primary" />

            <p className="font-sans text-xl font-semibold leading-[30px]">
              Add {name}'s parents
            </p>

            {parentsList.map((parent, i) => (
              <ParentCard
                key={parent.id}
                parent={parent}
                index={i}
                onChange={handleParentChange}
                errors={errors}
                errPrefix="parent"
              />
            ))}

            <button
              type="button"
              onClick={() => syncParents([...parentsList, createEmptyParent()])}
              className="flex w-full items-center justify-center gap-2 rounded-md border border-primary py-3 text-sm font-medium text-primary hover:bg-subtle"
            >
              <Plus className="size-4" />
              Add another
            </button>
          </div>
        )}
      </div>

      {showSiblings && (
        <div className="flex flex-col gap-4">
          <div className="border-t border-primary" />

          <div className="flex flex-col gap-1">
            <p className="font-sans text-xl font-semibold leading-[30px]">
              Did {name} have any siblings?
            </p>
            <p className="text-sm text-secondary tracking-[-0.28px]">
              Include half-siblings. The court treats them the same as full siblings.
            </p>
          </div>

          <RadioGroup
            name="has_siblings"
            value={hasSiblings}
            onValueChange={handleSiblingsSelect}
            className="flex flex-col gap-3"
          >
            <OptionCard label="Yes" selected={hasSiblings === "yes"} onClick={() => handleSiblingsSelect("yes")} variant="radio" value="yes" />
            <OptionCard label="No siblings" selected={hasSiblings === "no_siblings"} onClick={() => handleSiblingsSelect("no_siblings")} variant="radio" value="no_siblings" />
          </RadioGroup>
          <FieldError message={errors?.has_siblings} />

          {hasSiblings === "no_siblings" && (
            <div className="flex gap-3 rounded-lg border border-primary bg-subtle p-4">
              <AlertTriangle className="size-5 shrink-0 text-secondary" />
              <div className="flex flex-col gap-1">
                <p className="text-sm font-semibold">This estate may need an attorney</p>
                <p className="text-sm text-secondary">
                  Without a spouse, children, parents, or siblings, determining who can serve
                  as administrator requires a legal review. We recommend speaking with a
                  probate attorney about next steps.
                </p>
              </div>
            </div>
          )}

          {hasSiblings === "yes" && (
            <div className="flex flex-col gap-4">
              <div className="border-t border-primary" />

              <p className="font-sans text-xl font-semibold leading-[30px]">
                Add {name}'s siblings
              </p>

              {siblingsList.map((sibling, i) => (
                <SiblingCard
                  key={sibling.id}
                  sibling={sibling}
                  index={i}
                  canRemove={siblingsList.length > 1}
                  onChange={handleSiblingChange}
                  onRemove={removeSibling}
                  errors={errors}
                  errPrefix="sibling"
                />
              ))}

              <button
                type="button"
                onClick={() => syncSiblings([...siblingsList, createEmptyChild()])}
                className="flex w-full items-center justify-center gap-2 rounded-md border border-primary py-3 text-sm font-medium text-primary hover:bg-subtle"
              >
                <Plus className="size-4" />
                Add another
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
